import Cta from '@atoms/Cta/Cta';
import Text from '@atoms/Text/Text';
import { IArticleCard } from '@models/IArticleCard';
import { IContentCard } from '@models/IContentCard';
import { IDownloadCard } from '@models/IDownloadCards';
import { ILink } from '@models/ILink';
import DisplayStyleEnum from '@models/enums/DisplayStyleEnum';
import SiteEnum from '@models/enums/SiteEnum';
import NavigationCarousel from '@molecules/NavigationCarousel/NavigationCarousel';
import classNames from 'classnames';
import { SwiperSlide } from 'swiper/react';

interface ICardListing {
  readonly site: SiteEnum;
  readonly title?: string;
  readonly displayAs: DisplayStyleEnum;
  readonly itemsPerRow: 2 | 3 | 4;
  readonly cta: ILink;
  readonly cards: React.ReactElement<
    IContentCard | IDownloadCard | IArticleCard
  >[];
}

const CardListing = ({
  site,
  title,
  displayAs,
  itemsPerRow,
  cta,
  cards,
}: ICardListing) => {
  const gridClasses = [
    // we use carouselItems length with the index of these array elements to determine the classes
    'tw-col-span-full md:tw-col-span-3 lg:tw-col-span-6',
    'tw-col-span-full md:tw-col-span-3 lg:tw-col-span-4',
    'tw-col-span-full md:tw-col-span-3 lg:tw-col-span-6 xl:tw-col-span-3',
  ];

  const carouselItems = cards.length < itemsPerRow ? cards.length : itemsPerRow;

  const ctaEl = cta ? (
    <Cta
      className="tw-ml-auto"
      site={site}
      text={cta.text}
      url={cta.url}
      target={cta.target}
    />
  ) : null;

  return (
    <section className="tw-w-full">
      {displayAs === 'grid' && (
        <>
          {(title || cta) && (
            <div className="tw-grid-container">
              <div className="tw-col-span-full tw-flex tw-items-center tw-justify-between tw-pb-sm md:tw-pb-md xxl:tw-pb-16">
                {title && (
                  <Text variant="headline" className="tw-text-grey-100">
                    {title}
                  </Text>
                )}
                {ctaEl && <div className="max-md:tw-hidden">{ctaEl}</div>}
              </div>
            </div>
          )}
          <div className="tw-grid-container tw-gap-y-4 md:tw-gap-y-8">
            {cards.map((card) => (
              <div
                key={card.props.title}
                className={classNames(
                  gridClasses[carouselItems - 2],
                  'tw-col-span-full'
                )}
              >
                {card}
              </div>
            ))}
          </div>
          {ctaEl && (
            <div className="tw-container tw-mt-lg md:tw-hidden">{ctaEl}</div>
          )}
        </>
      )}
      {displayAs === 'carousel' && (
        <NavigationCarousel
          site={site}
          title={title}
          id={`${title}-content-cards-carousel`}
          numberOfSlides={cards.length}
          slidesToShowTabletLandscape={
            carouselItems < 3 ? 2 : carouselItems - 1
          }
          slidesToShowDesktop={carouselItems}
          slidesToShowLargeDesktop={carouselItems}
        >
          {cards.map((card) => (
            <SwiperSlide
              className="stretch-card tw-h-full"
              key={card.props.title}
            >
              {card}
            </SwiperSlide>
          ))}
        </NavigationCarousel>
      )}
    </section>
  );
};

export default CardListing;
